/**
 * Journal Entry Modal for creating new journal entries
 * Matches the old frontend's journal-entry-modal
 */
import { useState } from 'react';
import { BookOpen } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Textarea,
} from '@/components/ui';
import { useLanguage } from '@/store';
import { getJournalEntryModalTranslation } from '@/utils';

interface JournalEntryData {
  content: string;
  moodBefore: string;
  moodAfter: string;
}

interface JournalEntryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The dialog resets and closes once this resolves; a rejection keeps it open with the text. */
  onSubmit: (data: JournalEntryData) => void | Promise<unknown>;
  /** Shown above the buttons, e.g. when saving failed. */
  error?: string | null;
}

const moodOptions = [
  { value: 'peaceful', translationKey: 'moodPeaceful' as const },
  { value: 'joyful', translationKey: 'moodJoyful' as const },
  { value: 'anxious', translationKey: 'moodAnxious' as const },
  { value: 'stressed', translationKey: 'moodStressed' as const },
  { value: 'calm', translationKey: 'moodCalm' as const },
  { value: 'energized', translationKey: 'moodEnergized' as const },
];

export function JournalEntryModal({ open, onOpenChange, onSubmit, error }: JournalEntryModalProps) {
  const language = useLanguage();
  const [content, setContent] = useState('');
  const [moodBefore, setMoodBefore] = useState('peaceful');
  const [moodAfter, setMoodAfter] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const t = (key: Parameters<typeof getJournalEntryModalTranslation>[0]) =>
    getJournalEntryModalTranslation(key, language);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSubmit({
        content,
        moodBefore,
        moodAfter,
      });
    } catch {
      // The caller shows the error; keep the text so nothing is lost.
      return;
    } finally {
      setIsSaving(false);
    }
    // Reset form
    setContent('');
    setMoodBefore('peaceful');
    setMoodAfter('');
    onOpenChange(false);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" onClose={handleClose}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            {t('title')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-6 pt-4 space-y-4">
          {/* Entry Content */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('entry')}
            </label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('entryPlaceholder')}
              rows={6}
              className="resize-none"
              required
            />
          </div>

          {/* Mood Before */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('moodBefore')}
            </label>
            <select
              value={moodBefore}
              onChange={(e) => setMoodBefore(e.target.value)}
              className="w-full bg-background border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {moodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.translationKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Mood After (Optional) */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('moodAfter')}
            </label>
            <select
              value={moodAfter}
              onChange={(e) => setMoodAfter(e.target.value)}
              className="w-full bg-background border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">
                {t('selectMood')}
              </option>
              {moodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.translationKey)}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <p role="alert" className="text-sm text-destructive">{error}</p>
          )}

          <DialogFooter className="pt-4">
            <Button type="button" variant="ghost" onClick={handleClose}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={isSaving || !content.trim()}>
              {t('saveEntry')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
